import React, { useState, useRef, useEffect } from 'react';
import { Share2, Copy, CheckCircle2, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { Practitioner } from '@/types/practitioner';
import { APP_CONFIG } from '@/config/app-config';
import QRCodeStyling from 'qr-code-styling';

interface PractitionerShareButtonProps {
  practitioner: Practitioner;
  className?: string;
}

export const PractitionerShareButton: React.FC<PractitionerShareButtonProps> = ({ 
  practitioner, 
  className = '' 
}) => {
  const { t } = useTranslation('practitioners');
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [qrCodeGenerated, setQrCodeGenerated] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  
  const profileUrl = `${APP_CONFIG.PRODUCTION_DOMAIN}/book-an-appointment/${practitioner.slug}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast({
        title: t('linkCopied'),
        description: t('linkCopiedDescription'),
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: t('copyError'),
        description: t('copyErrorDescription'),
        variant: 'destructive',
      });
    }
  };

  const generateQRCode = () => {
    if (qrRef.current && !qrCodeGenerated) {
      qrRef.current.innerHTML = '';
      
      const qrCode = new QRCodeStyling({
        width: 200,
        height: 200,
        data: profileUrl,
        margin: 8,
        dotsOptions: {
          color: '#92400E', // amber-800
          type: 'rounded'
        },
        cornersSquareOptions: {
          color: '#78350F', // amber-900
          type: 'extra-rounded'
        },
        cornersDotOptions: {
          color: '#78350F', // amber-900
          type: 'dot'
        },
        backgroundOptions: {
          color: '#FFFBEB' // amber-50
        },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: 8
        }
      });
      
      qrCode.append(qrRef.current);
      setQrCodeGenerated(true);
    }
  };


  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={className}>
          <Share2 className="h-4 w-4 mr-2" />
          {t('shareButton')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={copyToClipboard}>
          {copied ? (
            <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
          ) : (
            <Copy className="h-4 w-4 mr-2" />
          )}
          {t('copyLink')}
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        <Dialog>
          <DialogTrigger asChild>
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <QrCode className="h-4 w-4 mr-2" />
              {t('qrCode')}
            </DropdownMenuItem>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('qrCodeTitle')}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200 shadow-sm">
                {qrCodeGenerated ? (
                  <div ref={qrRef} className="rounded-xl overflow-hidden" />
                ) : (
                  <div className="w-[200px] h-[200px] bg-amber-100/50 rounded-xl flex items-center justify-center">
                    <Button onClick={generateQRCode} variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100">
                      {t('generateQR')}
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground text-center">
                {t('qrCodeDescription')}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};